import { DynamoDBDocumentClient, PutCommand, ScanCommand, QueryCommand } from '@aws-sdk/lib-dynamodb';
import awsConfig from '../config/aws-config';
import { v4 as uuidv4 } from 'uuid';
import { getTrackedDynamoDBClient } from './aws-core';

export interface AuditLogEntry {
    id: string;
    action: string; // e.g., 'USER_INVITE', 'ROLE_UPDATE', 'USER_BLOCK'
    performedBy: string; // Email of admin
    targetUserId?: string;
    targetUserEmail?: string;
    details: string;
    timestamp: string;
    ipAddress?: string;
}

export class AWSAuditService {
    private docClient: DynamoDBDocumentClient | null = null;
    private tableName = awsConfig.dynamodb.TABLES.AUDIT_LOGS;

    private getClient(): DynamoDBDocumentClient {
        return getTrackedDynamoDBClient();
    }

    /**
     * Log an admin action
     */
    async logAction(
        action: string,
        performedBy: string,
        details: string,
        targetUserEmail?: string,
        targetUserId?: string
    ): Promise<void> {
        try {
            const client = this.getClient();
            const logEntry: AuditLogEntry = {
                id: uuidv4(),
                action,
                performedBy,
                details,
                targetUserEmail,
                targetUserId,
                timestamp: new Date().toISOString()
            };

            const params = {
                TableName: this.tableName,
                Item: logEntry
            };

            await client.send(new PutCommand(params));
            console.log('Audit log created:', logEntry.id);
        } catch (error) {
            console.error('Failed to create audit log:', error);
            // Don't throw, as we don't want to block the actual action if logging fails
        }
    }

    /**
     * Get recent audit logs
     */
    async getRecentLogs(limit: number = 50): Promise<AuditLogEntry[]> {
        try {
            const client = this.getClient();
            // Since DynamoDB Scan doesn't support descending sort effectively on timestamp without an index,
            // we will fetch and sort in memory for now (assuming log volume isn't massive yet).
            // For production with high volume, a GSI on timestamp is required.
            const params = {
                TableName: this.tableName,
                Limit: limit * 2 // Fetch a bit more to allow for sorting
            };

            const result = await client.send(new ScanCommand(params));

            const logs = (result.Items as AuditLogEntry[]) || [];
            return logs.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()).slice(0, limit);
        } catch (error) {
            console.error('Failed to fetch audit logs:', error);
            return [];
        }
    }
}

export const awsAuditService = new AWSAuditService();
